import { api } from '@goaltree/backend/convex/_generated/api'
import type { EventView } from '@goaltree/backend/convex/events'
import { eventPhase } from '@goaltree/backend/convex/lib/events'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AllDayBand, BusyStripRow, EventStripRow, NowLine } from '@/components/event-row'
import { TodaySectionHeader } from '@/components/today-section-header'
import { useNow } from '@/hooks/use-now'
import type { BusyBlock } from '@/hooks/use-today-tasks'
import { todayKey } from '@/lib/day'
import { addLocalDays, dedupeBusy, localDateTime } from '@/lib/events'

export const UPCOMING_DAYS = 14

function useTodayStart() {
  return localDateTime(todayKey())
}

// One subscription shared by the header badge and the Coming up panel.
export function useUpcomingWindow() {
  const from = useTodayStart()
  return {
    from,
    data: useQuery(api.events.upcoming, { from, to: addLocalDays(from, UPCOMING_DAYS) }),
  }
}

type Row =
  | { kind: 'event'; at: number; end: number; event: EventView }
  | { kind: 'busy'; at: number; end: number }

export function TodaySchedule({ busy, className }: { busy: BusyBlock[]; className?: string }) {
  const { t } = useTranslation()
  const now = useNow()
  const from = useTodayStart()
  const to = addLocalDays(from, 1)
  const events = useQuery(api.events.range, { from, to })

  const { allDay, rows } = useMemo(() => {
    const list = events ?? []
    const timedEvents = list.filter((e) => !e.allDay)
    const eventSpans = timedEvents.map((e) => ({ start: e.startsAt, end: e.endsAt }))
    const timed: Row[] = [
      ...timedEvents.map((e) => ({
        kind: 'event' as const,
        at: e.startsAt,
        end: e.endsAt,
        event: e,
      })),
      ...dedupeBusy(
        busy.filter((b) => b.end > from && b.start < to),
        eventSpans,
      ).map((b) => ({ kind: 'busy' as const, at: b.start, end: b.end })),
    ].sort((a, b) => a.at - b.at)
    return { allDay: list.filter((e) => e.allDay), rows: timed }
  }, [events, busy, from, to])

  if (!allDay.length && !rows.length) return null

  const allEvents = events ?? []
  const done = allEvents.filter((e) => eventPhase(e, now) === 'done').length
  const left = allEvents.length - done
  // Finished rows stay in time order, struck through, as in Everyday.
  const past = rows.filter((r) => r.end <= now)
  const ahead = rows.filter((r) => r.end > now)
  const nowIdx = ahead.findIndex((r) => r.at > now)
  const before = nowIdx === -1 ? ahead : ahead.slice(0, nowIdx)
  const after = nowIdx === -1 ? [] : ahead.slice(nowIdx)
  const showNow = (past.length > 0 || before.length > 0) && after.length > 0
  const nextId = ahead.find((r) => r.kind === 'event')?.event._id

  const renderRow = (r: Row) =>
    r.kind === 'event' ? (
      <EventStripRow key={r.event._id} event={r.event} now={now} next={r.event._id === nextId} />
    ) : (
      <BusyStripRow key={`busy-${r.at}-${r.end}`} start={r.at} end={r.end} past={r.end <= now} />
    )

  return (
    <section className={cn('mb-4', className)} aria-label={t('todaySchedule.title')}>
      <TodaySectionHeader
        label={t('todaySchedule.title')}
        count={
          done > 0
            ? t('todaySchedule.doneLeft', { done, left })
            : t('todaySchedule.left', { count: left })
        }
        className="mt-0 mb-2"
      />
      <AllDayBand events={allDay} now={now} />
      {past.map(renderRow)}
      {before.map(renderRow)}
      {showNow && <NowLine now={now} />}
      {after.map(renderRow)}
    </section>
  )
}
